import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, UserPlus } from 'lucide-react';
import { getBackendUrl } from '../utils/getBackendUrl';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

export const CreateSupervisorForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  
  // Add inline style to force black text - AGGRESSIVE VERSION
  React.useEffect(() => {
    console.log('CreateSupervisorForm: Applying BLACK text fix');
    
    const style = document.createElement('style');
    style.id = 'supervisor-form-black-text-fix';
    style.innerHTML = `
      input[id^="supervisor-"] {
        color: #000000 !important;
        background-color: #ffffff !important;
        -webkit-text-fill-color: #000000 !important;
      }
      input[id^="supervisor-"]::-webkit-input-placeholder {
        color: #9ca3af !important;
      }
      input[id^="supervisor-"]::placeholder {
        color: #9ca3af !important;
      }
    `;
    document.head.appendChild(style);
    
    // Also force inline styles directly on elements after a short delay
    setTimeout(() => {
      const inputs = document.querySelectorAll('input[id^="supervisor-"]');
      inputs.forEach(input => {
        input.style.color = '#000000';
        input.style.backgroundColor = '#ffffff';
        input.style.setProperty('color', '#000000', 'important');
        input.style.setProperty('background-color', '#ffffff', 'important');
        console.log(`Applied black text to ${input.id}`);
      });
    }, 100);
    
    return () => {
      const existingStyle = document.getElementById('supervisor-form-black-text-fix');
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.password.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/create-supervisor`, {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || "",
        address: "",
        role: "supervisor",
        password: formData.password
      });

      toast.success('Supervisor created successfully!');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Error creating supervisor:', error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Email already exists');
      } else {
        toast.error('Failed to create supervisor');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Version indicator - remove after confirming fix */}
      <div style={{ fontSize: '10px', color: '#666', marginBottom: '8px' }}>
        Form Version: 1.1.0-GLOBAL-CSS-FIX
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="supervisor-name" className="text-slate-700 font-semibold">
            Full Name *
          </Label>
          <Input
            id="supervisor-name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Enter full name"
            className="mt-1 bg-white border-slate-300 placeholder:text-slate-400 focus:border-purple-500"
            style={{ color: '#000000', backgroundColor: '#ffffff' }}
            required
          />
        </div>

        <div>
          <Label htmlFor="supervisor-email" className="text-slate-700 font-semibold">
            Email Address *
          </Label>
          <Input
            id="supervisor-email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="Enter email address"
            className="mt-1 bg-white border-slate-300 placeholder:text-slate-400 focus:border-purple-500"
            style={{ color: '#000000', backgroundColor: '#ffffff' }}
            required
          />
        </div>

        <div>
          <Label htmlFor="supervisor-phone" className="text-slate-700 font-semibold">
            Phone Number
          </Label>
          <Input
            id="supervisor-phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="Enter phone number"
            className="mt-1 bg-white border-slate-300 placeholder:text-slate-400 focus:border-purple-500"
            style={{ color: '#000000', backgroundColor: '#ffffff' }}
          />
        </div>

        <div>
          <Label htmlFor="supervisor-password" className="text-slate-700 font-semibold">
            Password *
          </Label>
          <Input
            id="supervisor-password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleInputChange}
            placeholder="Enter password"
            className="mt-1 bg-white border-slate-300 placeholder:text-slate-400 focus:border-purple-500"
            style={{ color: '#000000', backgroundColor: '#ffffff' }}
            required
          />
        </div>

        <div className="md:col-span-2">
          <Label htmlFor="supervisor-confirm-password" className="text-slate-700 font-semibold">
            Confirm Password *
          </Label>
          <Input
            id="supervisor-confirm-password"
            name="confirmPassword"
            type="password"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            placeholder="Confirm password"
            className="mt-1 bg-white border-slate-300 placeholder:text-slate-400 focus:border-purple-500"
            style={{ color: '#000000', backgroundColor: '#ffffff' }}
            required
          />
        </div>
      </div>

      <div className="pt-4">
        <Button
          type="submit"
          disabled={isLoading}
          className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Creating Supervisor...
            </>
          ) : (
            <>
              <UserPlus className="w-4 h-4 mr-2" />
              Create Supervisor
            </>
          )}
        </Button>
      </div>

      <div className="text-sm text-slate-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
        <p className="font-semibold text-slate-700 mb-1">Note:</p>
        <p>Supervisors have the same privileges as administrators and can manage all aspects of the system.</p>
      </div>
    </form>
  );
};